<template>
  <transition name="fade">
    <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/50" @click="closeSchedule" />

      <div class="relative bg-white rounded-xl shadow-xl w-full h-full max-w-sm sm:max-w-md md:max-w-2xl lg:max-w-4xl max-h-full overflow-hidden flex flex-col">
        <!-- New Iframe Content -->
        <div class="flex flex-col h-full">
          <!-- Header with close button -->
          <div class="flex justify-between items-center p-3 sm:p-4 border-b border-gray-200 flex-shrink-0">
            <h3 class="text-lg sm:text-xl font-semibold text-gray-900">นัดหมาย/เริ่มทดลองใช้ฟรี</h3>
            <button @click="closeSchedule" class="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0">
              <svg class="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          
          <!-- Iframe container -->
          <div class="flex-1 w-full overflow-hidden">
            <iframe 
              src="https://ecobzthai.com/form/demo-register"
              class="w-full h-full border-0"
              title="Demo Register Form"
              loading="lazy">
            </iframe>
          </div>
        </div>

        </div>

      <!-- Original Calendar Content (Hidden for now) -->
      <div class="hidden">
        <!-- Step 1: Calendar Selection -->
        <div v-if="currentStep === 1">
          <!-- Header -->
          <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 class="text-xl font-semibold text-gray-900">
                {{ titleMap[context.type] || 'นัดหมาย/เริ่มทดลองใช้ฟรี' }}
              </h3>
              <p class="text-sm text-gray-500">เลือกวันและเวลาที่สะดวกสำหรับคุณ</p>
            </div>
            <button @click="closeSchedule" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Calendar Body -->
          <div class="px-0 py-4">
            <div class="w-full">
              <!-- Calendar Header -->
              <div class="flex items-center justify-between mb-3 px-4">
                <div class="flex items-center gap-2">
                  <button @click="prevMonth" class="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" aria-label="เดือนก่อนหน้า">
                    <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
                  </button>
                  <h3 class="text-lg font-semibold text-gray-800">{{ monthLabel }}</h3>
                  <button @click="nextMonth" class="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" aria-label="เดือนถัดไป">
                    <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
                  </button>
                  <button @click="goToToday" class="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors" aria-label="ไปยังเดือนปัจจุบัน">
                    วันนี้
                  </button>
                </div>
                <div v-if="selectedDateTime" class="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-lg">
                  เลือกแล้ว: {{ formatSelectedDateShort() }}
                </div>
              </div>

              <!-- Calendar Grid -->
              <div class="bg-white border-l-0 border-r-0 border-t border-b border-gray-200 overflow-hidden">
                <!-- Weekday header -->
                <div class="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
                  <div v-for="w in weekdayLabels" :key="w" class="px-2 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200 last:border-r-0">
                    {{ w }}
                  </div>
                </div>

                <!-- Calendar body -->
                <div class="grid grid-cols-7">
                  <div
                    v-for="day in monthGrid"
                    :key="day.key"
                    :class="[
                      'border-r border-b border-gray-200 last:border-r-0 min-h-[80px] p-1.5 relative',
                      day.inMonth ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50',
                      isSelectedDay(day) ? 'bg-blue-50 border-blue-300' : '',
                      isToday(day.date) ? 'bg-yellow-50' : '',
                      isHoliday(day.date) && day.inMonth ? 'bg-gray-100' : ''
                    ]"
                  >
                    <!-- Day number -->
                    <div class="flex items-center justify-between mb-1.5">
                      <span :class="[
                        'text-xs font-medium',
                        day.inMonth ? 'text-gray-900' : 'text-gray-400',
                        isToday(day.date) ? 'text-blue-600 font-bold' : '',
                        isHoliday(day.date) && day.inMonth ? 'text-gray-500' : ''
                      ]">
                        {{ day.date.getDate() }}
                      </span>
                      <span v-if="isToday(day.date)" class="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                        วันนี้
                      </span>
                    </div>

                    <!-- Time slots (only for current month days, not holidays, not past) -->
                    <div v-if="day.inMonth && !isPast(day.date) && !isHoliday(day.date)" class="space-y-0.5">
                      <!-- Morning slot -->
                      <button
                        :disabled="isBusySession(day,'am') || isSessionPast(day,'am')"
                        @click="selectSession(day,'am')"
                        :class="[
                          'w-full text-[10px] px-1.5 py-1 rounded text-left transition-colors',
                          isSelectedSession(day,'am') ? 'bg-blue-600 text-white font-medium' : 
                          (isBusySession(day,'am') || isSessionPast(day,'am')) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 
                          'bg-green-100 text-green-700 hover:bg-green-200'
                        ]"
                      >
                        <div class="flex items-center justify-between">
                          <span class="font-medium">เช้า</span>
                          <span class="text-[9px]">{{ amRange.start }}-{{ amRange.end }}</span>
                        </div>
                      </button>

                      <!-- Afternoon slot -->
                      <button
                        :disabled="isBusySession(day,'pm') || isSessionPast(day,'pm')"
                        @click="selectSession(day,'pm')"
                        :class="[
                          'w-full text-[10px] px-1.5 py-1 rounded text-left transition-colors',
                          isSelectedSession(day,'pm') ? 'bg-blue-600 text-white font-medium' : 
                          (isBusySession(day,'pm') || isSessionPast(day,'pm')) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 
                          'bg-green-100 text-green-700 hover:bg-green-200'
                        ]"
                      >
                        <div class="flex items-center justify-between">
                          <span class="font-medium">บ่าย</span>
                          <span class="text-[9px]">{{ pmRange.start }}-{{ pmRange.end }}</span>
                        </div>
                      </button>
                    </div>

                    <!-- Past day indicator -->
                    <div v-else-if="day.inMonth && isPast(day.date)" class="text-[10px] text-gray-400 mt-1.5">
                      ผ่านแล้ว
                    </div>
                  </div>
                </div>
              </div>

              <!-- Legend -->
              <div class="flex items-center justify-center gap-4 mt-3 text-[10px] text-gray-600 px-4">
                <div class="flex items-center gap-1">
                  <div class="w-2 h-2 rounded bg-green-100 border border-green-200"></div>
                  <span>ว่าง</span>
                </div>
                <div class="flex items-center gap-1">
                  <div class="w-2 h-2 rounded bg-red-100 border border-red-200"></div>
                  <span>ไม่ว่าง</span>
                </div>
                <div class="flex items-center gap-1">
                  <div class="w-2 h-2 rounded bg-blue-600"></div>
                  <span>เลือกแล้ว</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 1 Footer -->
          <div class="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            <div class="text-xs text-gray-500">{{ titleMap[context.type] || context.type }}<span v-if="context.service"> • {{ serviceMap[context.service] }}</span></div>
            <div class="flex items-center gap-2">
              <button @click="closeSchedule" class="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm">ยกเลิก</button>
              <button :disabled="!selectedDateTime" @click="goToStep2" class="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm">
                ถัดไป: กรอกข้อมูล
              </button>
            </div>
          </div>
        </div>

        <!-- Step 2: Form -->
        <div v-else-if="currentStep === 2">
          <!-- Header -->
          <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 class="text-xl font-semibold text-gray-900">กรอกข้อมูลการนัดหมาย</h3>
              <p class="text-sm text-gray-500">{{ formatSelectedDate() }}</p>
            </div>
            <button @click="closeSchedule" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Form Body -->
          <div class="px-6 py-6">
            <div class="max-w-2xl mx-auto">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm text-gray-700 mb-2">ชื่อ-นามสกุล</label>
                  <input v-model="form.name" type="text" class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="ชื่อผู้ติดต่อ" />
                </div>
                <div>
                  <label class="block text-sm text-gray-700 mb-2">อีเมล</label>
                  <input v-model="form.email" type="email" class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="name@example.com" />
                </div>
                <div>
                  <label class="block text-sm text-gray-700 mb-2">เบอร์โทร</label>
                  <input v-model="form.phone" type="tel" class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="08x-xxx-xxxx" />
                </div>
                <div>
                  <label class="block text-sm text-gray-700 mb-2">บริษัท/องค์กร</label>
                  <input v-model="form.company" type="text" class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="ชื่อบริษัท" />
                </div>
                <div class="md:col-span-2">
                  <label class="block text-sm text-gray-700 mb-2">บริการที่สนใจ</label>
                  <select v-model="form.service" class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">เลือกบริการ</option>
                    <option value="erp">วางระบบ ERP</option>
                    <option value="marketing">การตลาดออนไลน์</option>
                    <option value="web">พัฒนาเว็บไซต์</option>
                    <option value="media">สตูดิโอผลิตสื่อ</option>
                  </select>
                </div>
                <div class="md:col-span-2">
                  <label class="block text-sm text-gray-700 mb-2">รายละเอียดเพิ่มเติม</label>
                  <textarea v-model="form.notes" rows="4" class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="ระบุความต้องการหรือคำถามเพิ่มเติม"></textarea>
                </div>
              </div>

              <!-- Status -->
              <div v-if="error" class="mt-4 text-sm text-red-600">{{ error }}</div>
              <div v-if="success" class="mt-4 text-sm text-green-600">ส่งคำขอสำเร็จ ทีมงานจะติดต่อกลับเพื่อยืนยันตารางนัดหมาย</div>
            </div>
          </div>

          <!-- Step 2 Footer -->
          <div class="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <button @click="goToStep1" class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">← กลับไปเลือกวันเวลา</button>
            <div class="flex items-center gap-3">
              <button @click="closeSchedule" class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">ยกเลิก</button>
              <button :disabled="submitting" @click="submit" class="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                <span v-if="submitting">กำลังส่ง...</span>
                <span v-else>ยืนยันการนัดหมาย</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
const { isOpen, context, form, closeSchedule, submit, submitting, success, error } = useScheduleModal()

// Step management
const currentStep = ref(1)
const selectedDateTime = computed(() => form.value.date && form.value.time)

const goToStep1 = () => { currentStep.value = 1 }
const goToStep2 = () => { currentStep.value = 2 }

// Reset to step 1 when modal opens
watch(isOpen, (open) => {
  if (open) currentStep.value = 1
})

const formatSelectedDate = () => {
  if (!form.value.date || !form.value.time) return ''
  const date = new Date(form.value.date)
  const sessionName = form.value.time === amRange.start ? 'ช่วงเช้า' : 'ช่วงบ่าย'
  const timeRange = form.value.time === amRange.start ? `${amRange.start}-${amRange.end}` : `${pmRange.start}-${pmRange.end}`
  return `${date.toLocaleDateString('th-TH', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} • ${sessionName} (${timeRange})`
}

const formatSelectedDateShort = () => {
  if (!form.value.date || !form.value.time) return ''
  const date = new Date(form.value.date)
  const sessionName = form.value.time === amRange.start ? 'เช้า' : 'บ่าย'
  return `${date.getDate()}/${date.getMonth()+1} ${sessionName}`
}

const titleMap = {
  trial: 'เริ่มทดลองใช้ฟรี',
  demo: 'นัดหมายสาธิตระบบ (ออนไลน์)',
  consult: 'นัดคุยความต้องการ',
  quote: 'ขอใบเสนอราคา',
}

const serviceMap = {
  erp: 'วางระบบ ERP',
  marketing: 'การตลาดออนไลน์',
  web: 'พัฒนาเว็บไซต์',
  media: 'สตูดิโอผลิตสื่อ',
}

// ==================== CONFIGURATION OPTIONS ====================
// ตั้งค่าต่างๆ ที่สามารถแก้ไขได้ง่าย
const calendarConfig = {
  // วันเริ่มต้นสัปดาห์ (0 = อาทิตย์, 1 = จันทร์)
  weekStartsOn: 0, // เริ่มต้นที่วันจันทร์
  
  // วันหยุดประจำสัปดาห์ (0 = อาทิตย์, 1 = จันทร์, ..., 6 = เสาร์)
  weeklyHolidays: [0, 6], // เสาร์-อาทิตย์
  
  // วันหยุดราชการ (รูปแบบ: 'YYYY-MM-DD')
  publicHolidays: [
    '2025-01-01', // วันปีใหม่
    '2025-02-12', // วันหยุดชดเชยวันขึ้นปีใหม่
    '2025-02-13', // วันหยุดชดเชยวันขึ้นปีใหม่
    '2025-02-14', // วันเลิกศก
    '2025-04-06', // วันจักรี
    '2025-04-13', // วันสงกรานต์
    '2025-04-14', // วันสงกรานต์
    '2025-04-15', // วันสงกรานต์
    '2025-05-01', // วันแรงงาน
    '2025-05-05', // วันฉัตรมงคล
    '2025-07-28', // วันเฉลิมพระชนมพรรษา
    '2025-08-12', // วันแม่
    '2025-10-13', // วันสวรรคต
    '2025-10-23', // วันปิยมหาราช
    '2025-12-05', // วันพ่อ
    '2025-12-10', // วันรัฐธรรมนูญ
    '2025-12-31', // วันสิ้นปี
  ],
  
  // ข้อความสำหรับวันหยุด
  holidayLabels: {
    weekly: 'วันหยุด',
    public: 'วันหยุดราชการ'
  }
}
// ==================== END CONFIGURATION ====================

// Calendar logic (month + sample availability)
const monthOffset = ref(0) // 0 = current month
const startOfWeek = (date) => {
  const d = new Date(date)
  const day = (d.getDay() - calendarConfig.weekStartsOn + 7) % 7
  d.setHours(0,0,0,0)
  d.setDate(d.getDate() - day)
  return d
}
const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d }
const addMonths = (date, n) => { const d = new Date(date); d.setMonth(d.getMonth() + n); return d }
const formatDate = (date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth()+1).padStart(2,'0')
  const day = String(date.getDate()).padStart(2,'0')
  return `${y}-${m}-${day}`
}
const thaiWeekdays = ['จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์', 'อาทิตย์']

// สร้างชื่อวันตามการตั้งค่าเริ่มต้นสัปดาห์
const weekdayLabels = computed(() => {
  const allDays = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']
  const orderedDays = []
  for (let i = 0; i < 7; i++) {
    const dayIndex = (calendarConfig.weekStartsOn + i) % 7
    orderedDays.push(allDays[dayIndex])
  }
  return orderedDays
})
const currentMonthStart = computed(() => {
  const base = new Date()
  const firstOfMonth = new Date(base.getFullYear(), base.getMonth(), 1)
  return addMonths(firstOfMonth, monthOffset.value)
})

const monthLabel = computed(() => {
  const d = currentMonthStart.value
  return d.toLocaleString('th-TH', { month: 'long', year: 'numeric' })
})

const startOfMonthGrid = computed(() => startOfWeek(currentMonthStart.value))
const endOfMonthGrid = computed(() => {
  // แสดงแค่ 6 สัปดาห์ (42 วัน) ตามมาตรฐานปฏิทิน
  const start = startOfMonthGrid.value
  const end = addDays(start, 41) // 42 วัน = 6 สัปดาห์
  return end
})

const monthGrid = computed(() => {
  const days = []
  const start = startOfMonthGrid.value
  const end = endOfMonthGrid.value
  for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
    days.push({
      key: formatDate(d),
      date: new Date(d),
      inMonth: d.getMonth() === currentMonthStart.value.getMonth(),
    })
  }
  return days
})

// Helper functions for holidays
const isWeeklyHoliday = (date) => {
  return calendarConfig.weeklyHolidays.includes(date.getDay())
}

const isPublicHoliday = (date) => {
  const dateStr = formatDate(date)
  return calendarConfig.publicHolidays.includes(dateStr)
}

const isHoliday = (date) => {
  return isWeeklyHoliday(date) || isPublicHoliday(date)
}

const getHolidayLabel = (date) => {
  if (isPublicHoliday(date)) return calendarConfig.holidayLabels.public
  if (isWeeklyHoliday(date)) return calendarConfig.holidayLabels.weekly
  return ''
}

// Session ranges
const amRange = { start: '11:00', end: '12:00' }
const pmRange = { start: '14:00', end: '15:00' }

// ==================== BOOKED APPOINTMENTS DATA ====================
// ข้อมูลการนัดหมายที่มีอยู่แล้ว - สามารถเชื่อมต่อกับ API ได้
const bookedAppointments = ref([
  // รูปแบบ: { date: 'YYYY-MM-DD', session: 'am'|'pm', customerName?: string }
  { date: '2025-08-30', session: 'am' },
  { date: '2025-08-30', session: 'pm' },
  { date: '2025-09-02', session: 'am' },
  { date: '2025-09-05', session: 'pm' },
  { date: '2025-09-10', session: 'am' },
  { date: '2025-09-12', session: 'pm' },
  { date: '2025-09-15', session: 'am' },
  { date: '2025-09-18', session: 'pm' },
  { date: '2025-09-20', session: 'am' },
  { date: '2025-09-25', session: 'pm' },
])

// ฟังก์ชันเพิ่มการนัดหมายใหม่ (สำหรับใช้กับ API)
const addBookedAppointment = (date, session, customerName = null) => {
  bookedAppointments.value.push({ date, session, customerName })
}

// ฟังก์ชันลบการนัดหมาย (สำหรับยกเลิกนัด)
const removeBookedAppointment = (date, session) => {
  const index = bookedAppointments.value.findIndex(
    appointment => appointment.date === date && appointment.session === session
  )
  if (index > -1) {
    bookedAppointments.value.splice(index, 1)
  }
}

// ฟังก์ชันตรวจสอบว่าช่วงเวลานั้นมีนัดแล้วหรือไม่
const isSessionBooked = (day, session) => {
  return bookedAppointments.value.some(
    appointment => appointment.date === day.key && appointment.session === session
  )
}
// ==================== END BOOKED APPOINTMENTS ====================

const isSameDate = (a, b) => {
  if (!a || !b) return false
  const da = new Date(a), db = new Date(b)
  return da.getFullYear()===db.getFullYear() && da.getMonth()===db.getMonth() && da.getDate()===db.getDate()
}

const isToday = (date) => {
  const now = new Date()
  return isSameDate(now, date)
}
const isPast = (date) => {
  const d = new Date(date); d.setHours(0,0,0,0)
  const now = new Date(); now.setHours(0,0,0,0)
  return d < now
}

const isSessionPast = (day, session) => {
  const now = new Date()
  const today = new Date(); today.setHours(0,0,0,0)
  const dayDate = new Date(day.date); dayDate.setHours(0,0,0,0)
  
  // ถ้าไม่ใช่วันนี้ ให้ใช้ isPast ปกติ
  if (dayDate.getTime() !== today.getTime()) {
    return isPast(day.date)
  }
  
  // ถ้าเป็นวันนี้ ให้ตรวจสอบเวลาปัจจุบัน
  const currentHour = now.getHours()
  const currentMinute = now.getMinutes()
  const currentTime = currentHour * 60 + currentMinute // แปลงเป็นนาที
  
  if (session === 'am') {
    // รอบเช้า 09:00-12:00 (540-720 นาที)
    const amEndTime = 12 * 60 // 12:00 = 720 นาที
    return currentTime >= amEndTime
  } else {
    // รอบบ่าย 13:30-17:00 (810-1020 นาที)
    const pmEndTime = 17 * 60 // 17:00 = 1020 นาที
    return currentTime >= pmEndTime
  }
}

const isBusySession = (day, session) => {
  // ตรวจสอบจากข้อมูลการนัดหมายที่มีอยู่แล้ว
  return isSessionBooked(day, session)
}

const isSelectedSession = (day, session) => {
  const time = session === 'am' ? amRange.start : pmRange.start
  return form.value.date === day.key && form.value.time === time
}

const selectSession = (day, session) => {
  if (isPast(day.date) || isBusySession(day, session) || isHoliday(day.date) || isSessionPast(day, session)) return
  form.value.date = day.key
  form.value.time = session === 'am' ? amRange.start : pmRange.start
}

const prevMonth = () => { monthOffset.value -= 1 }
const nextMonth = () => { monthOffset.value += 1 }
const goToToday = () => { monthOffset.value = 0 }

const isSelectedDay = (day) => form.value.date === day.key
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .2s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
